import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserCheck, Search, UserMinus, ArrowLeft } from "lucide-react";

const API = "/api";

export default function ActiveStudents() {
  const { getAuthHeader } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deactivating, setDeactivating] = useState(null);

  const fetchActive = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/students/active?limit=500`);
      const data = response.data;
      setStudents(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      // Retry once on server/network error
      if (!error.response || error.response.status >= 500) {
        try {
          await new Promise(r => setTimeout(r, 1500));
          const retry = await axios.get(`${API}/students/active?limit=500`);
          const data = retry.data;
          setStudents(Array.isArray(data) ? data : data.items || []);
          return;
        } catch (retryErr) {
          console.error("Retry also failed:", retryErr);
        }
      }
      console.error("Failed to fetch active students:", error);
      toast.error("Failed to load active students");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  const handleDeactivate = async (studentId, name) => {
    setDeactivating(studentId);
    try {
      await axios.put(`${API}/students/${studentId}/status`, { status: "inactive" }, { headers: getAuthHeader() });
      toast.success(`${name} has been marked inactive`);
      fetchActive();
    } catch (error) {
      toast.error("Failed to deactivate student");
    } finally {
      setDeactivating(null);
    }
  };

  const filtered = students.filter(s =>
    !searchQuery.trim() || s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="active-students-page">
      <Link to="/" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Dashboard
      </Link>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-emerald-100">
            <UserCheck className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Active Students</h1>
            <p className="text-sm text-slate-500">{students.length} active student{students.length !== 1 ? "s" : ""} currently enrolled</p>
          </div>
        </div>
        <Link to="/inactive-students">
          <Button variant="outline" size="sm" className="text-slate-600" data-testid="view-inactive-link">
            <UserMinus className="w-4 h-4 mr-1.5" />
            View Inactive
          </Button>
        </Link>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold">All Active Students</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search student name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-active-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">{searchQuery ? `No students match "${searchQuery}"` : "No active students"}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Student Name</TableHead>
                  <TableHead className="font-semibold text-center">Remaining Credits</TableHead>
                  <TableHead className="font-semibold text-center">Purchased</TableHead>
                  <TableHead className="font-semibold text-center">Last Class</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                  <TableHead className="text-right font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((student) => (
                  <TableRow key={student.id} data-testid={`active-student-${student.id}`}>
                    <TableCell>
                      <Link to={`/students/${student.id}`} className="flex items-center gap-3 hover:opacity-80">
                        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center">
                          <span className="text-emerald-700 font-semibold text-xs">
                            {student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <span className="font-medium text-slate-900">{student.name}</span>
                      </Link>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium border ${
                        student.remaining_classes <= 3
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}>
                        {student.remaining_classes}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-slate-600">
                      {student.total_purchased_classes}
                    </TableCell>
                    <TableCell className="text-center text-slate-600">
                      {student.last_class_date || "No classes yet"}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Active
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeactivate(student.id, student.name)}
                        disabled={deactivating === student.id}
                        className="text-slate-600 hover:text-red-600 hover:border-red-200"
                        data-testid={`deactivate-btn-${student.id}`}
                      >
                        <UserMinus className="w-3.5 h-3.5 mr-1.5" />
                        {deactivating === student.id ? "..." : "Deactivate"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
